/**
 * The error the API layer raises when it cannot obtain an access token.
 *
 * 🔴 **Why this type exists — a live incident, and 45 days of telemetry saying the same
 * thing.** `conversionsApi` used to build every request like this:
 *
 * ```ts
 * const token = await getAccessToken();          // null on FOUR different failures
 * if (token) headers['Authorization'] = `Bearer ${token}`;
 * ```
 *
 * When the token could not be acquired, the guard did not stop the request — it merely
 * omitted the header and **sent it anyway**. The API answered `401`, `fetchApi` threw a
 * generic `Error`, react-query set `isError`, and `ClientPicker` rendered *"The client
 * directory could not be reached."*
 *
 * **That message names the wrong system.** On 2026-08-13 it sent a triage session through
 * the service-principal secret, token acquisition, and Dataverse's response to the exact
 * OData query — every one of which came back healthy, because all of them sit *downstream
 * of an auth check the request never passed*. The session closed the incident as
 * "unexplained-and-cleared-by-restart". It was neither unexplained nor cleared.
 *
 * The Function App's own logs said so the whole time, at severity 2:
 * `401 SearchClients: No authorization header`. Over 45 days, **every single one of the 38
 * `SearchClients` invocations was rejected that way, and 78 of 80 calls across the entire
 * authenticated surface** — the app has essentially never served an authorized request.
 *
 * ⚠ **`client_IP` is `0.0.0.0` on traces, so telemetry cannot say which of those calls were
 * a browser and which were probes.** The count does not prove the browser is affected; the
 * *code path* does, and it is unconditional. What the count does prove is that a tokenless
 * request is the app's ordinary outcome rather than an exotic one.
 *
 * The fix is to make "no token" a **thrown, typed, named** condition rather than a missing
 * header — so the request is never sent, and the UI can say what actually went wrong.
 */

/**
 * Why a token could not be obtained. Kept as a union rather than a free string so a new
 * failure path has to be named here, where the UI copy is decided, instead of arriving as
 * an unhandled variant.
 */
export type AuthFailureReason =
  /** MSAL had not finished initializing. Should be unreachable — React renders after init. */
  | 'not-initialized'
  /** No active account: signed out, cache cleared, or another tab signed out underneath us. */
  | 'no-account'
  /** Silent acquisition needs a UI. MSAL is mid-redirect; the request must be abandoned. */
  | 'interaction-required'
  /** Anything else — a network blip, an AAD hiccup, a consent problem. */
  | 'acquisition-failed';

/**
 * No access token was available, so **no request was sent**.
 *
 * The distinction from a 401 matters to the caller: a 401 means the server rejected us,
 * while this means we never asked. Both used to look identical from the UI's side, which
 * is how an auth failure came to be reported as a directory outage.
 */
export class AuthRequiredError extends Error {
  readonly reason: AuthFailureReason;

  constructor(reason: AuthFailureReason, message?: string) {
    super(message ?? `No access token available (${reason}); request not sent.`);
    this.name = 'AuthRequiredError';
    this.reason = reason;
  }
}

/**
 * Whether an unknown thrown value is an auth failure.
 *
 * ⚠ Deliberately **not** `instanceof`. This is checked in render paths against errors that
 * have passed through react-query, and a duplicated module instance — two copies of the
 * class, one per bundle chunk — makes `instanceof` quietly false. That failure mode returns
 * the UI to the exact wrong message this whole change exists to remove, and it would do so
 * silently. Matching on `name` survives it.
 */
export function isAuthRequiredError(error: unknown): error is AuthRequiredError {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'AuthRequiredError'
  );
}

